import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { FundRequest, TransactionDefault, TransactionTravel, TransactionFood } from '~/types/fundrequests';
import { toUSD } from '~/utils';

interface CreatePartialDisbursementModalProps {
    isOpen: boolean;
    onClose: () => void;
    fundRequest: FundRequest | null;
    onSubmit: (partialAmounts: PartialAmounts) => Promise<void>;
    availableFunds?: number;
    rawAvailableFunds?: number;
    approvedPendingTotal?: number;
    unapprovedTotal?: number;
}

interface LineItemAdjustment {
    id: number;
    description: string;
    originalAmount: number | null;
    partialAmount: number;
    category: string;
    type: 'default' | 'travel' | 'food';
}

interface PartialAmounts {
    lineItems: LineItemAdjustment[];
    totalPartial: number;
    totalRemainder: number;
}

// Category mappings for display
const categoryLabels: { [key: string]: string } = {
    fundraising: 'Fundraising',
    medical: 'Medical',
    travel_mileage: 'Travel/Mileage',
    food_incidentals: 'Food/Incidentals',
    medications: 'Medications',
    insurance: 'Insurance',
    caregiving: 'Caregiving',
    physical_therapy: 'Physical Therapy',
    relocation_lodging: 'Relocation/Lodging',
    other: 'Other',
};

const CreatePartialDisbursementModal: React.FC<CreatePartialDisbursementModalProps> = ({
    isOpen,
    onClose,
    fundRequest,
    onSubmit,
    availableFunds,
    rawAvailableFunds,
    approvedPendingTotal,
    unapprovedTotal,
}) => {
    const [lineItems, setLineItems] = useState<LineItemAdjustment[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!fundRequest || !isOpen) return;

        const items: LineItemAdjustment[] = [];

        // Process default transactions
        const processDefaultTransactions = (transactions: TransactionDefault[], categoryKey: string) => {
            transactions?.forEach(transaction => {
                items.push({
                    id: transaction.id,
                    description: transaction.description,
                    originalAmount: Number(transaction.amount || 0),
                    partialAmount: 0,
                    category: categoryLabels[categoryKey] || categoryKey,
                    type: 'default',
                });
            });
        };

        // Process travel transactions
        const processTravelTransactions = (transactions: TransactionTravel[]) => {
            transactions?.forEach(transaction => {
                const hasAmount = transaction.amount !== null && transaction.amount !== undefined;
                const normalizedAmount = hasAmount ? Number(transaction.amount) : null;

                items.push({
                    id: transaction.id,
                    description: transaction.destination,
                    originalAmount: Number.isNaN(normalizedAmount ?? 0) ? null : normalizedAmount,
                    partialAmount: 0,
                    category: 'Travel/Mileage',
                    type: 'travel',
                });
            });
        };

        // Process food transactions
        const processFoodTransactions = (transactions: TransactionFood[]) => {
            transactions?.forEach(transaction => {
                items.push({
                    id: transaction.id,
                    description: transaction.recipient_name || 'Food/Incidentals',
                    originalAmount: Number(transaction.amount || 0),
                    partialAmount: 0,
                    category: 'Food/Incidentals',
                    type: 'food',
                });
            });
        };

        // Process all transaction types
        processDefaultTransactions(fundRequest.transactions_fundraising || [], 'fundraising');
        processDefaultTransactions(fundRequest.transactions_medical || [], 'medical');
        processDefaultTransactions(fundRequest.transactions_medications || [], 'medications');
        processDefaultTransactions(fundRequest.transactions_insurance || [], 'insurance');
        processDefaultTransactions(fundRequest.transactions_relocation_lodging || [], 'relocation_lodging');
        processDefaultTransactions(fundRequest.transactions_caregiving || [], 'caregiving');
        processDefaultTransactions(fundRequest.transactions_physical_therapy || [], 'physical_therapy');
        processDefaultTransactions(fundRequest.transactions_other || [], 'other');
        processTravelTransactions(fundRequest.transactions_travel || []);
        processFoodTransactions(fundRequest.transactions_food || []);

        setLineItems(items);
    }, [fundRequest, isOpen]);

    const handlePartialAmountChange = (itemId: number, value: string) => {
        const numericValue = parseFloat(value) || 0;
        
        setLineItems(prevItems => 
            prevItems.map(item => {
                if (item.id === itemId) {
                    if (item.originalAmount === null) {
                        return item;
                    }
                    // Ensure partial amount doesn't exceed original amount
                    const newPartialAmount = Math.min(numericValue, item.originalAmount);
                    return { ...item, partialAmount: Math.max(0, newPartialAmount) };
                }
                return item;
            })
        );
    };

    const calculateTotals = () => {
        const totalPartial = lineItems.reduce((sum, item) => {
            if (item.originalAmount === null) {
                return sum;
            }
            return sum + item.partialAmount;
        }, 0);
        const totalRemainder = lineItems.reduce((sum, item) => {
            if (item.originalAmount === null) {
                return sum;
            }
            return sum + (item.originalAmount - item.partialAmount);
        }, 0);
        return { totalPartial, totalRemainder };
    };

    const { totalPartial, totalRemainder } = calculateTotals();
    
    // Check if partial amount exceeds available funds
    const exceedsAvailableFunds = availableFunds !== undefined && totalPartial > availableFunds;

    const handleSubmit = async () => {
        if (totalPartial <= 0) return;

        setIsLoading(true);
        try {
            await onSubmit({
                lineItems,
                totalPartial,
                totalRemainder,
            });
            onClose();
        } catch (error) {
            console.error('Error creating partial disbursement:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (isLoading) return;
        onClose();
    };

    if (!isOpen) return null;

    // Group items by category
    const groupedItems = lineItems.reduce((acc, item) => {
        if (!acc[item.category]) {
            acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
    }, {} as { [key: string]: LineItemAdjustment[] });

    return (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-lg flex flex-col" style={{ maxHeight: 'calc(100vh - 80px)' }}>
                    <button
                        className={`absolute right-4 top-4 text-gray-600 hover:text-gray-900 z-10 ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        <RxCross2 size={24} />
                    </button>
                    
                    <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                    <h2 className="mb-6 text-[28px]/[36px] font-bold text-gray-900">
                        Create a Partial Disbursement
                    </h2>
                    
                    <p className="mb-6 text-gray-700">
                        Adjust the amounts for each line item. The partial amount will be disbursed now, and the remainder will create a new fund request for future disbursement.
                    </p>

                    {/* Request Amount and Funds Summary */}
                    <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="text-sm font-medium text-gray-600 mb-1">REQUEST AMOUNT</div>
                                <div className="text-4xl font-bold text-gray-900">
                                    {toUSD(lineItems.reduce((sum, item) => 
                                        sum + (item.originalAmount ?? 0), 0
                                    ))}
                                </div>
                                {availableFunds !== undefined && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="flex items-center gap-1">
                                            <span className="font-bold" style={{ color: '#EF4444' }}>
                                                -{toUSD(Math.max(0, (lineItems.reduce((sum, item) => 
                                                    sum + (item.originalAmount ?? 0), 0
                                                ) - availableFunds)))}
                                            </span>
                                            <span className="font-medium" style={{ color: '#EF4444' }}>(Available)</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 text-right">
                                {rawAvailableFunds !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">Current: {toUSD(rawAvailableFunds)}</div>
                                    </div>
                                )}
                                {approvedPendingTotal !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">Approved: {toUSD(approvedPendingTotal)}</div>
                                    </div>
                                )}
                                {unapprovedTotal !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">Unapproved: {toUSD(unapprovedTotal)}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="grid grid-cols-4 gap-4 mb-4 text-sm font-semibold text-gray-700 border-b pb-2">
                            <div>Description</div>
                            <div className="text-center">Request</div>
                            <div className="text-center">Partial Amount</div>
                            <div className="text-center">Remainder</div>
                        </div>

                        {Object.entries(groupedItems).map(([category, items]) => (
                            <div key={category} className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                                    {category}
                                </h3>
                                {items.map((item) => {
                                    const remainder = item.originalAmount === null
                                        ? null
                                        : item.originalAmount - item.partialAmount;
                                    return (
                                        <div key={item.id} className="grid grid-cols-4 gap-4 py-3 border-b border-gray-100 items-center">
                                            <div className="text-cyan-600 font-medium">
                                                {item.description}
                                            </div>
                                            <div className="text-center">
                                                {item.originalAmount === null ? 'N/A' : toUSD(item.originalAmount)}
                                            </div>
                                            <div className="text-center">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={item.originalAmount ?? undefined}
                                                    step="0.01"
                                                    value={item.partialAmount || ''}
                                                    onChange={(e) => handlePartialAmountChange(item.id, e.target.value)}
                                                    className="w-24 px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder={item.originalAmount === null ? 'N/A' : '$0.00'}
                                                    disabled={isLoading || item.originalAmount === null}
                                                />
                                            </div>
                                            <div className="text-center">
                                                {remainder === null ? 'N/A' : toUSD(remainder)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Totals */}
                    <div className="border-t-2 border-gray-800 pt-4 mb-6">
                        <div className="grid grid-cols-2 gap-8">
                            <div className="text-center">
                                <div className="text-sm font-medium text-gray-600 mb-1">Partial Request</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {toUSD(totalPartial)}
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-sm font-medium text-gray-600 mb-1">Additional Request</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {toUSD(totalRemainder)}
                                </div>
                            </div>
                        </div>
                        <div className="text-center mt-4 pt-4 border-t border-gray-200">
                            <div className="text-sm font-medium text-gray-600 mb-1">TOTAL for disbursement</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {toUSD(totalPartial + totalRemainder)}
                            </div>
                        </div>
                    </div>

                    {/* Warning Message */}
                    {exceedsAvailableFunds && availableFunds !== undefined && (
                        <div className="mt-4 p-4 bg-red-100 border-2 border-red-500 rounded-lg">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-500">
                                        Insufficient Funds Warning
                                    </h3>
                                    <div className="mt-2 text-sm text-red-500">
                                        <p>
                                            The partial disbursement amount of <strong>{toUSD(totalPartial)}</strong> exceeds the available funds of <strong>{toUSD(availableFunds)}</strong>. 
                                            This request may be declined due to insufficient funds.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Sticky Footer with Buttons */}
                <div className="border-t border-gray-200 p-6 bg-white rounded-b-lg">
                    <div className="flex justify-start space-x-4">
                        <button
                            onClick={handleCancel}
                            disabled={isLoading}
                            className={`rounded-full border-[2px] border-gray-300 px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-gray-700 transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50 ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={totalPartial <= 0 || isLoading}
                            className="rounded-full bg-[#F59E0B] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-200 hover:bg-[#D97706] disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2"
                        >
                            {isLoading && (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            )}
                            CONTINUE
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreatePartialDisbursementModal; 
